<script lang="ts">
	import type { Booleanish, FocusEventHandler, HTMLInputAttributes } from 'svelte/elements';
	import { combineClass } from '../util';

	interface $$Props extends HTMLInputAttributes {
		icon?: string | undefined;
		message?: string | undefined;
		value?: string | undefined;
		ariaInvalid?: Booleanish | undefined;
		formatType?: 'PHONE' | 'DATE' | 'NONE' | 'NUMBER' | 'BRN' | 'NUM&ENG' | 'BOOKCODE';
		focusOut?: FocusEventHandler<HTMLInputElement> | null | undefined;
	}

	export let icon: string | undefined = undefined;
	export let value: string | undefined = undefined;
	export let message: string | undefined = undefined;
	export let ariaInvalid: Booleanish | undefined = undefined;
	export let formatType = 'NONE';
	export let focusOut: FocusEventHandler<HTMLInputElement> | null | undefined = undefined;
	const isError = message !== undefined;

	const { class: clazz, ...inputProps } = $$restProps;
	import { createEventDispatcher } from 'svelte';
	function formatPattern(value: string) {
		if (formatType == 'PHONE') {
			const numbers = value.replace(/\D/g, '').slice(0, 11);

			if (numbers.startsWith('02')) {
				return numbers.length === 9
					? numbers.replace(/(02)(\d{3})(\d{4})/, '$1-$2-$3')
					: numbers.replace(/(02)(\d{3,4})(\d{4})/, '$1-$2-$3');
			} else if (numbers.length === 10) {
				return numbers.replace(/(0\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
			} else if (numbers.length === 11) {
				return numbers.replace(/(0\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
			}
			return numbers;
		}
		if (formatType == 'DATE') {
			const birth = value.replace(/\D/g, '').slice(0, 8);
			return birth.replace(/^(\d{4})(\d{2})(\d{2})$/, `$1-$2-$3`);
		}

		if (formatType == 'BRN') {
			const brn = value.replace(/\D/g, '').slice(0, 10);
			return brn.replace(/^(\d{3})(\d{2})(\d{5})$/, `$1-$2-$3`);
		}
		if (formatType == 'BOOKCODE') {
			const bookcode = value.toUpperCase().replace(/\W/g, '').slice(0, 16);
			return bookcode.replace(/(.{4})(.{4})(.{4})(.{4})/, '$1-$2-$3-$4');
		}

		if (formatType == 'NUMBER') return value.replace(/\D/g, '');
		if (formatType == 'NUM&ENG') return value.replace(/[^a-zA-Z0-9 ]/g, '');

		return value;
	}
	const dispatch = createEventDispatcher();
	function handleInput(event: Event) {
		const inputElement = event.target as HTMLInputElement;
		const inputValue = inputElement.value;
		value = formatPattern(inputValue);
		dispatch('input', value);
	}
	function handleKeydown(event: Event) {
		dispatch('keydown', event);
	}
</script>

<label
	class="input-container"
	hidden={$$restProps.type == 'hidden'}
	class:error-container={isError}
>
	<div class={combineClass('input-wrapper', clazz)} class:!bg-gray-50={$$restProps.disabled}>
		{#if icon !== undefined}
			<img src={icon} alt="icon for field" />
		{/if}
		<input
			on:input={handleInput}
			on:blur={handleInput}
			on:focusout={focusOut}
			on:keydown={handleKeydown}
			bind:value
			aria-invalid={ariaInvalid}
			class="w-full"
			class:!text-gray-300={$$restProps.disabled}
			class:placeholder:!text-gray-300={$$restProps.disabled}
			{...inputProps}
		/>
	</div>
	{#if isError}
		<span class="input-message">{message}</span>
	{/if}
</label>

<style lang="postcss">
	.input-container {
		@apply flex flex-col gap-1;

		&[hidden] {
			@apply hidden;
		}
		&:focus-within:not(.error-container) > .input-message {
			@apply text-primary-100;
		}
	}

	.input-wrapper {
		@apply flex
        px-4
        py-[18px]
        gap-[6px]
        justify-center
        items-center
        rounded-xl
        border
        border-gray-100
		bg-white;

		&:focus-within {
			@apply border-primary-100;
		}
		&:focus-within img {
			filter: invert(69%) sepia(56%) saturate(5296%) hue-rotate(193deg) brightness(98%)
				contrast(85%);
		}
	}

	.input-wrapper > img {
		@apply w-[18px]
        h-[18px];

		filter: invert(67%) sepia(63%) saturate(0%) hue-rotate(233deg) brightness(91%) contrast(99%);
	}

	.input-wrapper > input {
		@apply grow bg-transparent;

		&:focus {
			@apply outline-none;
		}
	}

	.input-message {
		@apply text-body-small
        leading-[15px]
        -tracking-[0.24px]
        text-gray-400
        font-normal;
	}

	.error-container {
		> .input-wrapper {
			@apply border-error-100;
			> input::placeholder {
				@apply text-error-100;
			}
			> img {
				filter: invert(30%) sepia(86%) saturate(3284%) hue-rotate(343deg) brightness(109%)
					contrast(118%);
			}
		}

		> .input-message {
			@apply text-error-100;
		}
	}

	input:-webkit-autofill {
		-webkit-box-shadow: 0 0 0 30px #fff inset;
	}
</style>
